import {List, ListItem, makeStyles, Typography} from "@material-ui/core";
import React from "react";
import Grid from "@material-ui/core/Grid";
import clsx from "clsx";
import PropTypes from "prop-types";
import {useI18N} from "../../../common/common";
import Box from "@material-ui/core/Box";
import {sumBy} from "lodash-es";
import TermsConditions from "./TermsConditions";
import {toDecimalString} from "../../../common/number";
import {hexToRgb} from "../../../common/theme";
import {formatDisplayDate} from "../../../common/date";
import {YEAR_MONTH_DAY_FORMAT} from "../../../common/constant";


const useStyles = makeStyles((theme) => ({
    flexVerticalCenter: {
        display: "flex",
        alignItems: "center"
    },
    sharesGTCEnd: {
        color: theme.palette.primary.main,
        cursor: "pointer"
    },
    totalPoints: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    expiredDatesItem: {
        color: hexToRgb(theme.palette.text.primary, theme.palette.textOpacity.textMedium)
    },
    paddingLeft: {
        paddingLeft: 0
    },
    offerListItem: {
        border: "1.5px solid " + theme.palette.grey[100],
        background: theme.palette.secondary[50],
        borderRadius: 6,
        marginBottom: theme.spacing(1),
        padding: theme.spacing(2)
    },
    offerPointsListItem: {
        border: "1.5px solid " + theme.palette.grey[100],
        borderRadius: 6,
        marginBottom: theme.spacing(1),
        paddingBottom: 0,
        paddingTop: 0
    },
    titles: {
        color: theme.palette.grey[900],
        marginBottom: theme.spacing(2)
    },
    textRight: {
        textAlign: "right"
    },
    flexColumn: {
        display: "flex",
        flexDirection: "column"
    },
    mb2: {
        marginBottom: theme.spacing(2)
    },
    mbminus1: {
        marginBottom: -theme.spacing(1)
    },
    p0: {
        padding: 0
    },
    mt1: {
        marginTop: theme.spacing(1)
    },
    mt2: {
        marginTop: theme.spacing(2)
    },
}));

const OfferOverview = ({
    shares,
    points,
    totalPoints,
    offerTotalPrice,
    sharesPrice,
    pointsPrice,
    showGTC
}) => {
    const classes = useStyles();
    const {i18n} = useI18N();

    const offerPointsPrices = sumBy(points, it => pointsPrice * (it?.points_amount ? it?.points_amount : it?.points_number));
    const offerSharesPrices = sharesPrice * shares?.length;

    const totalPrice = +offerSharesPrices + offerPointsPrices;

    return (
        <div>
            {shares?.length > 0 && sharesPrice > 0 && (
                <List
                    disablePadding
                    component="nav"
                >
                    {shares?.map((share, index) => {
                        return (
                            <ListItem
                                className={clsx(classes.offerListItem)}
                                key={index}
                            >
                                <Grid
                                    container
                                    className={classes.flexVerticalCenter}
                                >
                                    <Grid
                                        item
                                        md={6}
                                        xs={6}
                                        sm={6}
                                    >
                                        <Typography
                                            variant="subtitle1"
                                        >
                                            {share.share_name + "*"}
                                        </Typography>
                                    </Grid>

                                    <Grid
                                        item
                                        md={6}
                                        xs={6}
                                        sm={6}
                                        className={classes.textRight}
                                    >
                                        <Typography variant="subtitle1">
                                            {"EUR"} {sharesPrice ? toDecimalString(sharesPrice, 2) : toDecimalString(shares?.[0]?.share_price, 2)}
                                        </Typography>
                                    </Grid>
                                    <Grid
                                        item
                                        md={12}
                                        xs={12}
                                        sm={12}
                                        className={classes.mt1}
                                    >
                                        <Typography variant="body1">
                                            {share.display_share_id || share.share_id}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </ListItem>
                        );
                    })}
                </List>
            )}

            {/* Points Listing*/}
            {points?.length > 0 && pointsPrice > 0 && (
                <List
                    component="nav"
                    disablePadding
                >

                    <ListItem className={clsx(classes.offerPointsListItem, classes.flexColumn)}>
                        <Grid container>
                            <Grid
                                item
                                md={7}
                                xs={7}
                                sm={7}
                            >
                                <Typography
                                    e2e-id="total-points"
                                    variant="subtitle1"
                                    className={clsx(classes.totalPoints)}
                                >
                                    {totalPoints} {i18n("common.label.points")}
                                </Typography>
                            </Grid>

                            <Grid
                                item
                                md={5}
                                xs={5}
                                sm={5}
                                className={classes.textRight}
                            >
                                <Typography
                                    variant="subtitle1"
                                    className={clsx(classes.totalPoints)}
                                >
                                    {"EUR"} {toDecimalString(offerPointsPrices, 2)}
                                </Typography>
                            </Grid>
                        </Grid>

                        {/*Expirations Details*/}
                        {points?.map((pointItem, index) => (
                            <Grid
                                className={classes.mb2}
                                container
                                key={index}
                            >
                                <Grid
                                    item
                                    md={7}
                                    xs={7}
                                    sm={7}
                                >
                                    <Typography
                                        variant="body1"
                                        className={clsx(classes.expiredDatesItem)}
                                    >
                                        {i18n("view.marketplace.offer_details.points_expires_on") + ": "}
                                        {formatDisplayDate(pointItem.expiration_date || pointItem.exp_date, YEAR_MONTH_DAY_FORMAT)}
                                    </Typography>
                                </Grid>

                                <Grid
                                    item
                                    md={5}
                                    xs={5}
                                    sm={5}
                                    className={classes.textRight}
                                >
                                    <Typography
                                        e2e-id="points-price"
                                        variant="body1"
                                        className={clsx(classes.expiredDatesItem)}
                                    >
                                        {pointItem?.points_amount || pointItem?.points_number}{"x"}
                                        {" EUR "} {toDecimalString(pointItem?.price_per_point || pointsPrice, 2)}
                                    </Typography>
                                </Grid>
                            </Grid>
                        ))
                        }
                    </ListItem>
                </List>
            )}

            {/*Total price label*/}
            {((shares?.length > 0 && sharesPrice > 0) || (points?.length > 0 && pointsPrice > 0) || offerTotalPrice) &&
                <ListItem className={clsx(classes.p0, classes.mbminus1, classes.mt2)}>
                    <Grid
                        item
                        md={12}
                        xs={12}
                        sm={12}
                    >
                        <Typography
                            e2e-id="total-price"
                            variant="h5"
                            className={clsx(classes.textRight)}
                        >
                            {i18n("common.label.total")}
                            <Box
                                component="span"
                                pl={5}
                            />
                            {"EUR"} {toDecimalString(offerTotalPrice || totalPrice, 2)}
                        </Typography>
                    </Grid>
                </ListItem>
            }

            {showGTC && (
                <TermsConditions />
            )}
        </div>
    );
};

OfferOverview.defaultProps = {
    showGTC: true
};

OfferOverview.propTypes = {
    points: PropTypes.array,
    shares: PropTypes.array,
    sharesPrice: PropTypes.number,
    pointsPrice: PropTypes.number,
    totalPoints: PropTypes.number,
    offerTotalPrice: PropTypes.number,
    showGTC: PropTypes.bool
};

export default OfferOverview;
